/**
 * Apps per person on Settings → Integrations (slice 6a §6.4; Paper 74,
 * 74b, and the chosen "Who can use it" dropdown, S3 and S3b): the Share
 * dropdown on each of the person's own rows, with its confirm, and Shared
 * with you, the others' shared accounts and servers, each with its
 * connector's avatar and no Manage.
 */
import { Button } from "@crew/ui/components/button";
import { PersonAvatar } from "@crew/ui/components/crew-avatar";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "@crew/ui/components/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "@crew/ui/components/popover";
import { cn } from "@crew/ui/lib/utils";
import { Check, ChevronDown, Lock, Users } from "lucide-react";
import { type ComponentProps, useState } from "react";
import { personTint } from "../../app/tints";
import { usePeople } from "../people/api";
import { AskFirstLink } from "./ask-first-page";
import { AppIcon, SectionHead, StateLabel } from "./integrations-parts";
import {
  firstName,
  JUST_YOU_WORDS,
  shareWords,
  sharedNote,
  sharedRowView,
  type SharedRow,
} from "./integrations-view";

type ShareProps = {
  shared: boolean;
  /** The row's name, for the trigger's label and the confirm. */
  label: string;
  /** How far a shared one reaches: "up to Read only", "with the tools you turned on". */
  reach: string;
  busy: boolean;
  onChange: (shared: boolean) => void;
};

/**
 * Who can use it (Paper S3, S3b): a dropdown that names the value, Just you
 * or Shared. Picking Shared confirms in place, once (spec §10, choice 5);
 * picking Just you applies at once. A popover beside the menu on a desktop,
 * a sheet from a line under the row's lines on a phone.
 */
export function ShareSelect({ at, ...props }: ShareProps & { at: "phone" | "desktop" }) {
  const [open, setOpen] = useState(false);
  const trigger = (
    <ShareTrigger shared={props.shared} label={props.label} busy={props.busy} at={at} />
  );
  const options = <ShareOptions {...props} onDone={() => setOpen(false)} />;
  if (at === "phone") {
    return (
      <div className="flex items-center justify-between gap-3 pt-2.5 pr-2 md:hidden">
        <span className="text-meta text-muted-foreground">Who can use it</span>
        <Drawer open={open} onOpenChange={setOpen} showSwipeHandle>
          <DrawerTrigger disabled={props.busy} render={trigger} />
          <DrawerContent className="gap-3.5 px-4 pt-2 pb-6 data-[swipe-direction=down]:rounded-t-2xl">
            <DrawerTitle className="px-1 text-lead font-semibold text-foreground">
              Who can use {props.label}?
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              Just you, or shared with the crew.
            </DrawerDescription>
            {options}
          </DrawerContent>
        </Drawer>
      </div>
    );
  }
  return (
    <div className="hidden w-35 shrink-0 justify-end md:flex">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger disabled={props.busy} render={trigger} />
        <PopoverContent align="end" className="w-80 gap-1 p-1.5">
          {options}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ShareTrigger({
  shared,
  label,
  busy,
  at,
  ...rest
}: ComponentProps<"button"> & {
  shared: boolean;
  label: string;
  busy: boolean;
  at: "phone" | "desktop";
}) {
  const Icon = shared ? Users : Lock;
  return (
    <button
      type="button"
      {...rest}
      data-share={shared ? "shared" : "just-you"}
      disabled={busy}
      aria-label={`Who can use ${label}: ${shared ? "Shared" : "Just you"}`}
      className={cn(
        "flex w-33 items-center gap-1.5 rounded-md border pr-2 pl-2.5 text-meta outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:opacity-60",
        at === "phone" ? "h-8.5" : "h-8",
        shared
          ? "border-moss/35 bg-moss/[0.07] font-semibold text-moss"
          : "border-input bg-card font-medium text-foreground",
      )}
    >
      <Icon className={cn("size-4 shrink-0", !shared && "text-muted-foreground")} aria-hidden />
      <span className="flex-1 text-left">{shared ? "Shared" : "Just you"}</span>
      <ChevronDown
        className={cn("size-3.5 shrink-0", !shared && "text-muted-foreground")}
        aria-hidden
      />
    </button>
  );
}

/** The two choices, the same in the popover and the sheet. */
function ShareOptions({
  shared,
  label,
  reach,
  busy,
  onChange,
  onDone,
}: ShareProps & { onDone: () => void }) {
  // Sharing asks once: picking Shared opens its confirm under it.
  const [asking, setAsking] = useState(false);
  return (
    <div className="flex flex-col gap-1" data-share-options>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (shared) onChange(false);
          onDone();
        }}
        className="flex items-start gap-2.5 rounded-md p-2.5 text-left hover:bg-accent"
      >
        <Lock className="mt-0.5 size-4 shrink-0 text-foreground" aria-hidden />
        <span className="flex flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-foreground">Just you</span>
          <span className="text-meta text-muted-foreground">{JUST_YOU_WORDS}</span>
        </span>
        {shared ? null : <Check className="mt-0.5 size-4 shrink-0 text-foreground" aria-hidden />}
      </button>
      <div
        className={cn(
          "flex flex-col gap-2.5 rounded-md",
          asking || shared ? "bg-moss/[0.07]" : "hover:bg-accent",
        )}
      >
        <button
          type="button"
          disabled={busy}
          onClick={() => (shared ? onDone() : setAsking(true))}
          aria-expanded={asking}
          className="flex items-start gap-2.5 p-2.5 pb-0 text-left last:pb-2.5"
        >
          <Users className="mt-0.5 size-4 shrink-0 text-moss" aria-hidden />
          <span className="flex flex-1 flex-col gap-0.5">
            <span className="text-sm font-semibold text-moss">Shared with the crew</span>
            <span className="text-meta text-copy">{shareWords(reach)}</span>
          </span>
          {shared ? <Check className="mt-0.5 size-4 shrink-0 text-moss" aria-hidden /> : null}
        </button>
        {asking ? (
          <div className="flex justify-end gap-2 px-2.5 pb-2.5">
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Cancel
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                onChange(true);
                setAsking(false);
                onDone();
              }}
            >
              Share {label}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The connector's avatar: their initials and tint from People, else from their name. */
function ConnectorAvatar({
  user,
  people,
}: {
  user: SharedRow["user"];
  people: Map<string, { initials: string; tint: string }>;
}) {
  const known = people.get(user.id);
  const initials =
    known?.initials ??
    user.name
      .split(/\s+/)
      .map((w) => w[0] ?? "")
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const tint = personTint(known?.tint ?? "sand");
  return (
    <>
      <PersonAvatar initials={initials} tint={tint} size={24} className="md:hidden" />
      <PersonAvatar initials={initials} tint={tint} size={22} className="hidden md:flex" />
    </>
  );
}

/** Shared with you (Paper 74, 74b): only shown when someone has shared something. */
export function SharedWithYou({
  rows,
  timeZone,
  now,
}: {
  rows: SharedRow[];
  timeZone: string;
  now: number;
}) {
  const people = usePeople();
  const byId = new Map((people.data?.people ?? []).map((p) => [p.id, p]));
  if (rows.length === 0) return null;
  return (
    <section
      aria-label="Shared with you"
      className="flex flex-col gap-2.5 pt-2 md:gap-3"
      data-shared-with-you
    >
      <SectionHead
        title="Shared with you"
        inline
        line={
          <>
            <span className="md:hidden">Only the person who shared one can change it.</span>
            <span className="hidden md:inline">
              Accounts others in the crew connected and shared. Only they can change them.
            </span>
          </>
        }
      />
      <div className="flex flex-col rounded-panel bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.08)]">
        {rows.map((row, i) => (
          <SharedRowView
            key={row.id}
            row={row}
            first={i === 0}
            people={byId}
            timeZone={timeZone}
            now={now}
          />
        ))}
      </div>
      <p className="text-meta leading-[18px] text-muted-foreground md:hidden">{sharedNote(rows)}</p>
    </section>
  );
}

function SharedRowView({
  row,
  first,
  people,
  timeZone,
  now,
}: {
  row: SharedRow;
  first: boolean;
  people: Map<string, { initials: string; tint: string }>;
  timeZone: string;
  now: number;
}) {
  const v = sharedRowView(row, timeZone, now);
  const icon =
    row.kind === "account" ? (
      <AppIcon app={row.app.app} icon={row.app.icon} mark={row.app.mark} />
    ) : (
      <AppIcon app={`mcp:${row.server.name}`} />
    );
  return (
    <div
      data-shared={row.id}
      data-status={row.kind === "account" ? row.account.status : row.server.status}
      className={cn(
        "flex items-center gap-3 px-4 py-3.5 md:gap-4 md:px-5 md:py-4",
        !first && "border-t border-border",
      )}
    >
      {icon}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-body leading-5 font-semibold text-foreground">
          {v.title}
        </span>
        <span className="truncate text-meta leading-[18px] text-muted-foreground">
          <span className="md:hidden">{v.underPhone}</span>
          <span className="hidden md:inline">{v.under}</span>
        </span>
        {v.ask ? (
          <span className="text-meta leading-[18px] text-copy" data-ask>
            {v.ask}
          </span>
        ) : null}
        {v.state.tone === "moss" ? null : (
          <StateLabel tone={v.state.tone} className="pt-1 md:hidden">
            {v.state.text}
          </StateLabel>
        )}
        {row.kind === "account" ? <AskFirstLink app={row.app.app} className="mt-1" /> : null}
      </div>
      <span className="hidden w-45 shrink-0 text-meta leading-[18px] text-copy md:block">
        {v.level}
      </span>
      <StateLabel tone={v.state.tone} className="hidden w-42.5 shrink-0 md:flex">
        {v.state.text}
      </StateLabel>
      <div className="flex shrink-0 items-center justify-end gap-2 md:w-52.5">
        <ConnectorAvatar user={row.user} people={people} />
        <span className="hidden text-meta leading-[18px] text-muted-foreground md:inline">
          Shared by {firstName(row.user.name)}
        </span>
      </div>
    </div>
  );
}
